import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import { ArchivePageContentProps } from "design-system/components/search/archive-page-content/archive-page-content";

import {
  useState,
  useEffect,
  useRef,
  KeyboardEventHandler,
  MouseEventHandler,
  useCallback,
  ChangeEvent,
} from "react";

import { useSearchParams } from "next/navigation";
import { useContentful } from "frontend/hooks/use-contentful";
import { useSearch } from "frontend/hooks/use-search";
import {
  SearchQuery,
  KnowledgeGraphData,
  FacultyKnowledgeGraphData,
  KnowledgeResponse,
  KnowledgeRestructuredInfobox,
} from "frontend/types";
import {
  ArchivePageFacets,
  ArchivePageFacetItem,
} from "design-system/components/search/archive-page-facets/archive-page-facets";
import { Page } from "design-system/components/primitives/pagination/pagination";
import { SearchBox } from "design-system/components/search/search-box/search-box";
import { SearchStats } from "design-system/components/search/search-stats/search-stats";
import { TeaseFeed } from "design-system/components/blocks/tease-feed/tease-feed";
import { createSearchTease } from "frontend/contentful/search/create-search-tease";
import {
  archiveSorts,
  archiveSortsLegacy,
  Facets,
  getInitialSelectedFacets,
  getInitialSelectedSorts,
  useDateRange,
} from "../pages/archive-page/utils";
import { LazyTeaseFeedTease } from "design-system/components/blocks/tease-feed/tease-feed-tease";

import { TeaseRow } from "design-system/components/blocks/tease-row/tease-row";
import { LazyTeaseRowTease } from "design-system/components/blocks/tease-row/tease-row-tease";
import { useSearchQuery } from "frontend/hooks/use-search-query";
import {
  ArchiveContentEntry,
  DatePickerFacetEntry,
  TermFacetEntry,
  AlphabeticalFacetEntry,
} from "frontend/contentful/schema/semantics";
import { ArticleTeaseProps } from "design-system/components/primitives/article-tease/article-tease";
import { DateRange } from "react-day-picker";
import { SearchDatePicker } from "design-system/components/search/search-date-picker/search-date-picker";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { useSearchDropdowns } from "frontend/hooks/use-search-dropdowns";
import { DataLayerEvent, useDataLayer } from "frontend/hooks/use-data-layer";
import { usePageProps } from "frontend/hooks/use-page-props";
import {
  InfoBox,
  InfoboxProps,
} from "design-system/components/blocks/infobox/infobox";
import { useFetch } from "frontend/hooks/use-fetch";
import { getSiteId } from "frontend/lib/get-site-id";
import { BREAKPOINTS } from "design-system/utils/constants";
import { useBreakpointMatches } from "design-system/hooks/use-breakpoint-matches";
import { SearchAlphabeticalPicker } from "design-system/components/search/search-alphabetical-picker/search-alphabetical-picker";
import { getInfoBox } from "../utils/get-infobox";
import { useHash } from "frontend/hooks/use-hash";

interface Options {
  slug?: string;
  headerAbove?: boolean;
}

declare global {
  interface WindowEventMap {
    hashchange: CustomEvent;
  }
}

interface Facet {
  name: string;
  count: number;
}

export const createArchiveContentProps = createContentfulProps<
  ArchiveContentEntry,
  ArchivePageContentProps,
  Options
>(({ entry, options }) => {
  const [currentTab, setCurrentTab] = useHash();
  const searchParams = useSearchParams();
  const { getEntry, getEntries } = useContentful();
  const { pushDataLayer } = useDataLayer();
  const page = usePageProps();

  const resultsRef = useRef<HTMLDivElement | null>(null);
  const headerAbove = options?.headerAbove || false;

  const getKey = useCallback(
    (key: string) => {
      if (options?.slug) {
        return `${options.slug}-${key}`;
      }

      return key;
    },
    [options?.slug],
  );

  const [currentPage, setCurrentPage] = useState<number>(
    parseInt(searchParams.get(getKey("page")) || "1", 10),
  );

  const tabTitle = options?.slug?.replace(/[^A-Za-z0-9]/g, "");
  const contentSourceEntry = getEntry(entry.fields.contentSource);
  const facetConfiguratorEntry = getEntry(entry.fields.facetConfig);
  const allFacetEntries = getEntries(facetConfiguratorEntry?.fields.facets);
  const termFacetEntries = allFacetEntries.filter<TermFacetEntry>(
    (facet): facet is TermFacetEntry =>
      isContentType<TermFacetEntry>("termFacet", facet),
  );
  const alphabeticalFacetEntries =
    allFacetEntries.filter<AlphabeticalFacetEntry>(
      (facet): facet is AlphabeticalFacetEntry =>
        isContentType<AlphabeticalFacetEntry>("alphabeticalFacet", facet),
    );

  const { dateRange, handleDatePickerSubmit, getEsDateFilters } =
    useDateRange(getKey);

  const { selectedOptions, getSelectedOptionsESFilters, resetSelectedOptions } =
    useSearchDropdowns();

  // Elasticsearch Facets ------------------------------------------------------
  const facetsToDisplay: Array<string> = [];
  termFacetEntries.forEach((facet) => {
    if (facet.fields.field) {
      facetsToDisplay.push(facet.fields.field);
    }
  });
  // Add alphabetical facet fields
  alphabeticalFacetEntries.forEach((facet) => {
    if (facet.fields.field) {
      facetsToDisplay.push(facet.fields.field);
    }
  });

  const [selectedFacets, setSelectedFacets] = useState<Facets>(
    getInitialSelectedFacets(
      searchParams,
      [...termFacetEntries, ...alphabeticalFacetEntries],
      getKey,
    ),
  );

  const [selectedSorts, setSelectedSorts] = useState<Facets>(
    getInitialSelectedSorts(searchParams, entry.fields.sortOptions || []),
  );
  const activeFacetList = Object.values(selectedFacets).flat();
  const prevUrl = useRef<string>();

  // Elasticsearch Sort ------------------------------------------------
  const sort = selectedSorts.sort
    ? selectedSorts.sort.join(",")
    : contentSourceEntry?.fields?.sort?.join(",");

  const size = parseInt(contentSourceEntry?.fields.limit || "10", 10);
  const from = size * (currentPage - 1);

  const resetSort = () => {
    const sort = contentSourceEntry?.fields?.sort;
    setSelectedSorts(() => ({
      sort: sort || [],
    }));
  };

  // Base elasticsearch query from content source entry
  let filters = "";
  if (contentSourceEntry?.fields.query) {
    filters += contentSourceEntry.fields.query;
  }

  // Add selected facets to elasticsearch query
  if (selectedFacets && Object.keys(selectedFacets).length > 0) {
    for (const [field, values] of Object.entries(selectedFacets)) {
      if (values.length > 0) {
        if (!filters.startsWith("(") || !filters.endsWith(")")) {
          filters = "(" + filters + ")";
        }

        const prefix = filters ? " AND " : "";
        filters += `${prefix}${field}:("${values.join('" OR "')}")`;
      }
    }
  }

  // Only add parentheses if we actually have filters
  if (filters && !filters.startsWith("(")) {
    filters = `(${filters})`;
  }

  // Ensure dropdownFilters are only added when they exist
  const dropdownFilters = getSelectedOptionsESFilters(filters);
  if (dropdownFilters) {
    if (filters) {
      if (!filters.startsWith("(")) {
        filters = `(${filters})`;
      }
      filters += dropdownFilters;
    } else {
      filters = dropdownFilters;
    }
  }

  const dateFilter = getEsDateFilters(filters);
  if (dateFilter) {
    filters += dateFilter;
  }

  const activeSelectedOptions = Object.values(selectedOptions).filter(Boolean);

  const scrollResultsIntoView = () => {
    const isReducedMotion =
      window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;
    if (!isReducedMotion) {
      // Scroll to top of results
      resultsRef.current?.scrollIntoView();
    }
  };

  const handlePageChange = (pageNumber: number, shouldScroll = true) => {
    setCurrentPage(pageNumber);
    if (shouldScroll) scrollResultsIntoView();
  };

  const resetPagination = () => {
    if (currentPage !== 1) handlePageChange(1, false);
  };

  const { searchQuery, debouncedQuery, setSearchQuery } = useSearchQuery({
    onSearchQueryChange: () => {
      resetPagination();
    },
  });

  const searchQueryIndex = contentSourceEntry?.fields.index || "modern";
  const searchQueryParams: SearchQuery = {
    facets: facetsToDisplay,
    index: searchQueryIndex,
    query: debouncedQuery,
    filters: filters || undefined, // Only include filters if they exist
    from,
    size,
    sort,
    facetLimit: alphabeticalFacetEntries.length > 0 ? 500 : undefined,
  };

  const { data } = useSearch(searchQueryParams);

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
    resetPagination();
  };

  const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSearchQuery(e.currentTarget.value);
      resetPagination();
    }
  };

  const handleMouseDown: MouseEventHandler<HTMLFormElement> = (e) => {
    const input = e.currentTarget?.previousElementSibling as HTMLInputElement;
    if (
      !e.currentTarget?.previousElementSibling?.classList.contains(
        "hbs-search-box__input",
      )
    ) {
      return;
    }
    e.preventDefault();
    setSearchQuery(input.value);
    resetPagination();
  };

  const pageEntry = getEntry(page.entry);
  const entryTemplate: string | undefined =
    pageEntry?.fields && "template" in pageEntry.fields
      ? (pageEntry?.fields.template as string)
      : undefined;

  useEffect(() => {
    // Handle browser back button
    window.onpopstate = (event) => {
      if (event.state) {
        history.go(0);
      }
    };
  });

  useEffect(() => {
    // Update URL query params
    const url = new URL(window.location.href);
    const testUrl = url;

    if (currentPage > 1) {
      url.searchParams.set(getKey("page"), currentPage.toString());
    } else {
      url.searchParams.delete(getKey("page"));
    }

    if (Object.entries(selectedFacets).length > 0) {
      for (const [facetField, facetValues] of Object.entries(selectedFacets)) {
        url.searchParams.delete(getKey(facetField));

        for (const facetValue of facetValues) {
          url.searchParams.append(getKey(facetField), facetValue);
        }
      }
    } else {
      termFacetEntries.forEach((termFacetEntry) => {
        const field = termFacetEntry.fields.field;
        if (field) {
          url.searchParams.delete(getKey(field));
        }
      });
      allFacetEntries.forEach((facetEntry) => {
        if (
          isContentType<AlphabeticalFacetEntry>("alphabeticalFacet", facetEntry)
        ) {
          const field = facetEntry.fields.field;
          if (field) {
            url.searchParams.delete(getKey(field));
          }
        }
      });
    }

    if (Object.entries(selectedSorts).length > 0) {
      for (const [sortField, sortValues] of Object.entries(selectedSorts)) {
        url.searchParams.delete(getKey(sortField));

        for (const sortValue of sortValues) {
          url.searchParams.append(getKey(sortField), sortValue);
        }
      }
    }

    if (!debouncedQuery && testUrl.searchParams.get("q")) {
      testUrl.searchParams.delete("q");
    }
    if (
      prevUrl.current &&
      prevUrl.current !== testUrl.href &&
      (allFacetEntries.length || currentPage > 1)
    ) {
      window.history.pushState({}, "", url);
    }
    prevUrl.current = url.href;
  }, [
    selectedFacets,
    selectedSorts,
    termFacetEntries,
    allFacetEntries,
    currentPage,
    getKey,
    debouncedQuery,
  ]);

  const prevVar1 = useRef(data);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (
      selectedFacets &&
      prevVar1.current !== data &&
      ("#" + tabTitle === currentTab || !tabTitle)
    ) {
      let facetFilterCategory = "";
      let facetFilterValue = "";
      let facetFilterFull = "";
      let facetFilterDepth = 0;
      if (selectedFacets) {
        for (const [field, values] of Object.entries(selectedFacets)) {
          if (values.length > 0) {
            facetFilterDepth += values.length;
            facetFilterCategory +=
              facetFilterCategory !== "" ? `,${field}` : field;
            facetFilterValue +=
              facetFilterValue.length !== 0
                ? `,${values.join(",")}`
                : values.join(",");
            facetFilterFull +=
              facetFilterFull.length !== 0
                ? `,${field}:${values.join(",")}`
                : `${field}:${values.join(",")}`;
          }
        }
      }
      const type = url.href.includes("search")
        ? "search-primary"
        : "search-secondary";
      /* eslint-disable camelcase */

      pushDataLayer({
        event: data && data.total < 1 ? "no_search_results" : "search_results",
        event_data: {
          search_term: url.searchParams.get("q") || undefined,
          search_numresults: data?.total || 0,
          search_type: type,
          search_type_ahead: false,
          search_result_page: !data || data.total < 1 ? 0 : currentPage,
          search_result_page_url: window.location.href.split("?")[0],
          search_result_tab: tabTitle,
          facet_filter_category: facetFilterCategory,
          facet_filter_value: facetFilterValue,
          facet_filter_full: facetFilterFull,
          facet_filter_depth: facetFilterDepth,
          explore_search: url.href.includes("/knowledge/explore"),
        },
      });
      /* eslint-enable camelcase */

      prevVar1.current = data;
      if (tabTitle && typeof setCurrentTab === "function") {
        setCurrentTab("#" + tabTitle);
      }
    }
  }, [
    currentPage,
    data,
    selectedFacets,
    getKey,
    pushDataLayer,
    currentTab,
    tabTitle,
  ]);

  // Add selected facets to elasticsearch query
  let facetFilterCategory = "";
  let facetFilterValue = "";
  let facetFilterFull = "";
  if (selectedFacets) {
    for (const [field, values] of Object.entries(selectedFacets)) {
      if (values.length > 0) {
        facetFilterCategory = field;
        facetFilterValue = values.join(",");
        facetFilterFull = `${field}:${values.join(",")}`;
        if (!filters.startsWith("(") || !filters.endsWith(")")) {
          filters = "(" + filters + ")";
        }

        const prefix = filters ? " AND " : "";
        filters += `${prefix}${field}:("${values.join('" OR "')}")`;
      }
    }
  }

  // Create pagination props
  const pages: Array<Page> = [];
  if (data?.total) {
    const numberOfPages = Math.ceil(data.total / size);
    for (const pageNumber of Array(numberOfPages).keys()) {
      const number = pageNumber + 1;
      const url = new URL(window.location.href);
      url.searchParams.set(getKey("page"), number.toString());
      pages.push({
        number,
        link: url.toString(),
        isCurrent: number === currentPage,
      });
    }
  }

  const teaseRowFeedItems: Array<LazyTeaseRowTease> = [];
  const teaseFeedItems: Array<LazyTeaseFeedTease> = [];
  const site = getEntry(page.site);
  const siteId = getSiteId(site);

  const dataLayerEvent: DataLayerEvent = {
    /* eslint-disable camelcase */
    event: "search_result_click",
    event_data: {
      search_term: searchQuery,
      search_result_page: currentPage,
      search_result_tab: tabTitle,
      facet_filter_category: facetFilterCategory,
      facet_filter_value: facetFilterValue,
      facet_filter_full: facetFilterFull,
      explore_search: page.request.path.includes("/knowledge/"),
      explore_search_info_box_clicked: "",
    },
    /* eslint-enable camelcase */
  };

  data?.hits?.forEach((hit, i) => {
    const teaseItem = createSearchTease(
      hit,
      searchQueryIndex,
      undefined,
      siteId,
      entryTemplate,
    );
    if (teaseItem) {
      if (teaseItem.props) {
        const handleOnSearchClick: MouseEventHandler<HTMLFormElement> = () => {
          /* eslint-disable camelcase */
          const title =
            teaseItem.props &&
            "title" in teaseItem.props &&
            teaseItem.props.title
              ? teaseItem.props?.title
              : undefined;
          const url =
            teaseItem.props && "link" in teaseItem.props && teaseItem.props.link
              ? teaseItem.props?.link
              : undefined;

          dataLayerEvent.event_data.search_result_title = title?.toString();
          dataLayerEvent.event_data.search_result_url = url;
          dataLayerEvent.event_data.search_result_position = i;
          pushDataLayer(dataLayerEvent);
          /* eslint-enable camelcase */
        };
        teaseItem.props.onClick = handleOnSearchClick;
      }
      if (entryTemplate === "Person Profiles") {
        teaseRowFeedItems.push(teaseItem as LazyTeaseRowTease);
      } else {
        if (teaseItem.name === "ArticleTease") {
          const teaseProps: ArticleTeaseProps | undefined = teaseItem.props;
          if (teaseProps?.byline) {
            //Disable "by" link for initial Search Launch
            teaseProps.byline.disableLinks = true;
          }
          if (teaseProps?.variant === "profile") {
            teaseProps.variant = "profile-search";
            if (teaseProps.image) teaseProps.image.aspectRatio = "1/1";
          }

          if (teaseProps) {
            teaseProps.showSource = true;
          }
        }

        teaseFeedItems.push(teaseItem as LazyTeaseFeedTease);
      }
    }
  });

  function handleClearAll() {
    setSearchQuery("");
    setSelectedFacets({});
    resetSort();
    resetPagination();
    resetSelectedOptions();
    handleDatePickerSubmit({ from: undefined, to: undefined });
  }
  const isDesktopXl = useBreakpointMatches(BREAKPOINTS.DESKTOP_XL);

  let showInfobox = false;
  let infoboxResults: InfoboxProps = { type: "", name: "" };
  if (site?.fields.slug === "knowledge") {
    let infobox: KnowledgeRestructuredInfobox = {};
    let infoBoxEmpty = true;
    const resp = useFetch<Array<unknown>>({
      url: "/api/fetch/knowledge-infobox",
      params: { query: encodeURIComponent(searchQuery) },
    });
    if (resp.error) {
      console.error("Error fetching knowledge infobox:", resp.error);
    } else {
      resp.data?.forEach((thisData) => {
        const box = JSON.parse(thisData as string) as KnowledgeResponse;
        if (box.error) {
          return;
        }
        if (box.body.restructuredInfobox && infoBoxEmpty) {
          infobox = box.body.restructuredInfobox;
          infoBoxEmpty = false;
          return;
        }
      });
      const props = getInfoBox(infobox);

      if (props) {
        let infoUrl = props.url;
        if (infoUrl && infoUrl.indexOf(".hbs.edu") > -1) {
          infoUrl = site.fields.searchPage && infoUrl.split(".hbs.edu")[1];
        }
        infoboxResults = {
          url: infoUrl,
          type: props.type,
          name: props.name,
          descriptionLabel:
            props.descriptionLabel !== props.description
              ? props.descriptionLabel
              : undefined,
          description: props.description,
          image: props.image?.src ? { src: props.image?.src } : undefined,
          recentPublications: props.recentPublications,
          align: "center",
          theme: "white",
        };
        if (props.link?.href) {
          infoboxResults.link = props.link;
        }
        if (props.intro) {
          infoboxResults.intro = props.intro;
        }
        if (props.metadata?.length) {
          infoboxResults.metadata = props.metadata;
        }
        // eslint-disable-next-line camelcase
        dataLayerEvent.event_data.explore_search_info_box = props.name;
      }

      showInfobox = Boolean(
        entry.fields.showInfobox && searchQuery && props?.name,
      );
    }
  } else {
    const priority = getEntry(page.siteArchivePageSettings)?.fields
      .infoBoxPriority;
    const companyKnowledgeGraphRes = useFetch<KnowledgeGraphData>(
      entry.fields.showInfobox
        ? {
            url: "/api/fetch/tooltip-graph",
            params: { query: searchQuery, type: "Companies" },
          }
        : null,
    );

    const facultyKnowledgeGraphRes = useFetch<FacultyKnowledgeGraphData>(
      entry.fields.showInfobox
        ? {
            url: "/api/fetch/tooltip-graph",
            params: { query: searchQuery, type: "Faculty" },
          }
        : null,
    );

    const isFacultyKnowledgeGraphRes = (
      knowledgeGraphData?: typeof knowledgeGraphRes.data,
    ): knowledgeGraphData is NonNullable<
      typeof facultyKnowledgeGraphRes.data
    > => Boolean(knowledgeGraphData && "faculty" in knowledgeGraphData);

    let knowledgeGraphRes =
      priority === "Faculty"
        ? facultyKnowledgeGraphRes
        : companyKnowledgeGraphRes;

    const isKnowledgeGraphResEmpty =
      !knowledgeGraphRes.data ||
      knowledgeGraphRes.data.search === "no results" ||
      knowledgeGraphRes.data.publications.length === 0 ||
      (isFacultyKnowledgeGraphRes(knowledgeGraphRes.data)
        ? !knowledgeGraphRes.data.faculty.name
        : false) ||
      knowledgeGraphRes.error;

    if (!knowledgeGraphRes.isLoading && isKnowledgeGraphResEmpty) {
      knowledgeGraphRes =
        priority === "Faculty"
          ? companyKnowledgeGraphRes
          : facultyKnowledgeGraphRes;
    }

    infoboxResults = {
      type: isFacultyKnowledgeGraphRes(knowledgeGraphRes.data)
        ? "Profile"
        : "Company",
      name: isFacultyKnowledgeGraphRes(knowledgeGraphRes.data)
        ? knowledgeGraphRes.data.faculty.name
        : knowledgeGraphRes.data?.search || "",
      descriptionLabel:
        isFacultyKnowledgeGraphRes(knowledgeGraphRes.data) &&
        knowledgeGraphRes.data.faculty.description
          ? "About"
          : undefined,
      description: isFacultyKnowledgeGraphRes(knowledgeGraphRes.data)
        ? knowledgeGraphRes.data.faculty.description
        : undefined,
      image:
        isFacultyKnowledgeGraphRes(knowledgeGraphRes.data) &&
        knowledgeGraphRes.data.faculty.imageUrl
          ? { src: knowledgeGraphRes.data.faculty.imageUrl }
          : undefined,
      link:
        isFacultyKnowledgeGraphRes(knowledgeGraphRes.data) &&
        knowledgeGraphRes.data.faculty.primaryUrl
          ? {
              href: knowledgeGraphRes.data.faculty.primaryUrl,
              children: "Full profile",
            }
          : undefined,
      recentPublications:
        knowledgeGraphRes.data?.publications.map(({ link, text }) => {
          return {
            children: text,
            href: link,
          };
        }) || [],
      align: "center",
      theme: "white",
    };

    showInfobox = Boolean(
      entry.fields.showInfobox &&
        searchQuery &&
        !knowledgeGraphRes.error &&
        infoboxResults.name &&
        infoboxResults.recentPublications &&
        infoboxResults.recentPublications.length > 0,
    );
  }

  const sortsArray = selectedSorts.sort;

  const sortOption = sortsArray ? sortsArray[0] : undefined;
  const defaultSort = contentSourceEntry?.fields?.sort
    ? contentSourceEntry.fields.sort[0]
    : undefined;
  const optionsArray = [];
  if (
    entry.fields.sortOptions &&
    entry.fields.sortOptions.length > 0 &&
    !defaultSort
  ) {
    optionsArray.push({ value: "reset", label: "Relevancy" });
  }

  if (entry.fields.sortOptions) {
    optionsArray.push(
      ...entry.fields.sortOptions.map((option) => ({
        value:
          searchQueryIndex === "classnotes" ||
          searchQueryIndex === "legacy" ||
          searchQueryIndex === "protected"
            ? archiveSortsLegacy[option]
            : archiveSorts[option],
        label: option,
      })),
    );
  }
  //build the sortByOptions object
  const sortByOptions =
    entry.fields.sortOptions && entry.fields.sortOptions.length > 0
      ? {
          options: optionsArray,
          onChange: (event: ChangeEvent<HTMLSelectElement>) => {
            if (event.target.value === "reset") {
              resetSort();
            } else {
              setSelectedSorts((prevSelectedFacets) => {
                const newSelectedFacets = { ...prevSelectedFacets };
                newSelectedFacets.sort = [event.target.value];
                return newSelectedFacets;
              });
            }
          },
          selectedOption:
            sortOption ||
            (contentSourceEntry?.fields?.sort
              ? contentSourceEntry.fields.sort[0]
              : undefined),
        }
      : undefined;

  return {
    aside: (
      <>
        {facetConfiguratorEntry?.fields?.showSearchBox && (
          <SearchBox
            searchTerm={searchQuery}
            handleSearch={handleSearchQueryChange}
            handleKeydownSearch={handleInputKeyDown}
            handleMousedownSearch={handleMouseDown}
            placeholder={facetConfiguratorEntry?.fields?.placeholder || ""}
            isSmall
          />
        )}

        {allFacetEntries?.map((facetEntry) => {
          if (isContentType<TermFacetEntry>("termFacet", facetEntry)) {
            const field = facetEntry.fields.field;
            if (!field) return null;

            const items: Array<ArchivePageFacetItem> = [];
            data?.facets?.[field]?.forEach((facet) => {
              items.push({
                name: facet.name,
                value: facet.name,
                checked: selectedFacets[field]?.includes(facet.name) || false,
                number: facet.count.toString(),
              });
            });

            return (
              <ArchivePageFacets
                key={field}
                title={facetEntry.fields.label}
                items={items}
                onFacetChanged={(checkbox, checked) => {
                  setSelectedFacets((prevSelectedFacets) => {
                    if (!checkbox.value) return prevSelectedFacets;

                    const newSelectedFacets = { ...prevSelectedFacets };

                    let newSelectedFacetValues;

                    if (checked) {
                      newSelectedFacetValues = [
                        ...(newSelectedFacets[field] || []),
                        checkbox.value,
                      ];
                    } else {
                      newSelectedFacetValues = newSelectedFacets[field]?.filter(
                        (value) => value !== checkbox.value,
                      );
                    }

                    if (newSelectedFacetValues) {
                      newSelectedFacets[field] = newSelectedFacetValues;
                    }

                    return newSelectedFacets;
                  });

                  resetPagination();
                }}
              />
            );
          } else if (
            isContentType<DatePickerFacetEntry>("datePickerFacet", facetEntry)
          ) {
            return (
              <SearchDatePicker
                key={facetEntry.fields.label ?? "date-picker"}
                selectedRange={dateRange}
                handleDatePickerSubmit={(range: DateRange) => {
                  handleDatePickerSubmit(range);
                  resetPagination();
                }}
                startDate="mm/dd/yyyy"
                endDate="mm/dd/yyyy"
                label={facetEntry.fields.label ?? ""}
              />
            );
          } else if (
            isContentType<AlphabeticalFacetEntry>(
              "alphabeticalFacet",
              facetEntry,
            )
          ) {
            const field = facetEntry.fields.field;
            if (!field) return null;

            // Use single query with increased size for facets
            const searchQuery: SearchQuery = {
              ...searchQueryParams,
              facets: [field],
              facetLimit: 500, // Increase limit to get all possible values
            };

            const results = useSearch(searchQuery);

            // Handle facets similar to term facets
            const allFacets: Array<Facet> = [];

            // Always include selected facets first
            const selectedFacetsForField = selectedFacets[field] || [];
            selectedFacetsForField.forEach((value) => {
              allFacets.push({
                name: value,
                count:
                  results.data?.facets?.[field]?.find((f) => f.name === value)
                    ?.count || 0,
              });
            });

            // Add remaining facets from results
            results.data?.facets?.[field]?.forEach((facet) => {
              if (!selectedFacetsForField.includes(facet.name)) {
                allFacets.push(facet);
              }
            });

            // Ensure selected facets remain visible even with no results
            const items = allFacets
              .concat(
                selectedFacetsForField
                  .filter(
                    (selected) => !allFacets.some((f) => f.name === selected),
                  )
                  .map((selected) => ({
                    name: selected,
                    count: 0,
                  })),
              )
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((facet) => ({
                name: facet.name,
                value: facet.name,
                checked: selectedFacetsForField.includes(facet.name),
                number: facet.count.toString(),
              }));

            return (
              <SearchAlphabeticalPicker
                key={field}
                title={facetEntry.fields.label}
                items={items}
                onFacetChanged={(item, checked) => {
                  setSelectedFacets((prevSelectedFacets) => {
                    const newSelectedFacets = { ...prevSelectedFacets };
                    if (checked && item.value) {
                      newSelectedFacets[field] = [
                        ...(newSelectedFacets[field] || []),
                        item.value,
                      ];
                    } else if (item.value) {
                      newSelectedFacets[field] = (
                        newSelectedFacets[field] || []
                      ).filter((value) => value !== item.value);
                    }
                    return newSelectedFacets;
                  });
                  resetPagination();
                }}
              />
            );
          }
          return null;
        })}
      </>
    ),
    children:
      entryTemplate === "Person Profiles" ? (
        <TeaseRow
          align={showInfobox && isDesktopXl ? "center" : "wide"}
          teaseStyle="full"
          teases={teaseRowFeedItems}
          wrap="wrap"
        />
      ) : (
        <TeaseFeed
          ref={resultsRef}
          align={showInfobox && isDesktopXl ? "center" : "wide"}
          teaseStyle="full"
          totalHits={data?.total}
          error={data?.error}
          searchInfo={
            <SearchStats
              totalHits={data?.total}
              searchQuery={searchQuery}
              facetValues={activeFacetList.concat(activeSelectedOptions)}
              dateRange={dateRange}
              onClearAll={handleClearAll}
            />
          }
          sortByOptions={sortByOptions}
          items={teaseFeedItems}
          HeadingLevel={headerAbove ? "h3" : "h2"}
        />
      ),

    pagination: {
      pages,
      size,
      onPageClick: (page: Page) => {
        handlePageChange(page.number);
      },
    },

    rightRail: showInfobox && (
      <InfoBox {...infoboxResults} dataLayer={dataLayerEvent} />
    ),
  } satisfies ArchivePageContentProps;
});
